import type { Html, HtmlBuilder } from "foldkit/html";
import * as lucide from "lucide";

import { i18next } from "../i18n";
import { icon } from "./icons";

/**
 * Knowledge folders: a note's folder is a path ("Projects/Atlas", "" = the
 * root; server `core/a2a/noteFolders.ts`). Folders are implicit, so the tree
 * is built from the server's folder counts (`GET /notes/folders`) plus
 * folders made in this session that hold no note yet.
 *
 * The tree is a disclosure list, not an ARIA tree: each folder is a link
 * (Tab reaches it, Enter opens it) with an expand button beside it, so it
 * needs no roving focus. Rows are drop targets while a note is dragged; the
 * note row's Move menu is the keyboard path for the same thing.
 */

export type FolderNode = {
	readonly path: string;
	readonly name: string;
	readonly depth: number;
	/** Notes in this folder and below. */
	readonly total: number;
	readonly hasChildren: boolean;
};

export const folderOf = (note: { readonly folder?: string }): string =>
	note.folder ?? "";

export const isUnder = (folder: string, prefix: string): boolean =>
	prefix === "" || folder === prefix || folder.startsWith(`${prefix}/`);

export const parentFolder = (path: string): string =>
	path.includes("/") ? path.slice(0, path.lastIndexOf("/")) : "";

export const folderName = (path: string): string =>
	path.slice(path.lastIndexOf("/") + 1);

/** "a/ /b//" → "a/b", as the server normalizes; "" is the root. */
export const cleanFolder = (raw: string): string =>
	raw
		.split("/")
		.map((s) => s.trim())
		.filter(Boolean)
		.join("/");

export const joinFolder = (parent: string, name: string): string =>
	cleanFolder(`${parent}/${name}`);

/** Every folder and its ancestors, parents first, siblings by name. */
export const buildTree = (
	noteFolders: ReadonlyArray<string>,
	extra: ReadonlyArray<string> = [],
): ReadonlyArray<FolderNode> => {
	const paths = new Set<string>();
	for (const folder of [...noteFolders, ...extra]) {
		const segments = folder.split("/").filter(Boolean);
		segments.forEach((_, i) => paths.add(segments.slice(0, i + 1).join("/")));
	}
	const sorted = [...paths]
		.map((p) => p.split("/"))
		.sort((a, b) => {
			for (let i = 0; i < Math.min(a.length, b.length); i++) {
				const c = (a[i] ?? "").localeCompare(b[i] ?? "");
				if (c !== 0) return c;
			}
			return a.length - b.length;
		})
		.map((s) => s.join("/"));
	return sorted.map((path) => ({
		path,
		name: folderName(path),
		depth: path.split("/").length,
		total: noteFolders.filter((f) => isUnder(f, path)).length,
		hasChildren: sorted.some((p) => p.startsWith(`${path}/`)),
	}));
};

/** The tree from `GET /notes/folders` (each folder's direct count) plus local-only folders. */
export const treeFromCounts = (
	counts: {
		readonly folders: ReadonlyArray<{
			readonly path: string;
			readonly count: number;
		}>;
	},
	extra: ReadonlyArray<string> = [],
): ReadonlyArray<FolderNode> =>
	buildTree(
		counts.folders.flatMap((f) =>
			Array.from({ length: f.count }, () => f.path),
		),
		extra,
	);

/** Every note in the circle: root notes plus each folder's direct count. */
export const countsTotal = (counts: {
	readonly rootCount: number;
	readonly folders: ReadonlyArray<{ readonly count: number }>;
}): number =>
	counts.rootCount + counts.folders.reduce((n, f) => n + f.count, 0);

/**
 * Top-level folders start open (two levels show) and deeper ones closed;
 * `toggled` flips that per path, so the default needs no init step.
 */
export const isExpanded = (
	path: string,
	toggled: ReadonlyArray<string>,
): boolean => !path.includes("/") !== toggled.includes(path);

/** Nodes whose every ancestor is open. */
export const visibleNodes = (
	nodes: ReadonlyArray<FolderNode>,
	toggled: ReadonlyArray<string>,
): ReadonlyArray<FolderNode> =>
	nodes.filter((node) => {
		const segments = node.path.split("/");
		return segments
			.slice(0, -1)
			.every((_, i) => isExpanded(segments.slice(0, i + 1).join("/"), toggled));
	});

type Glyph = "Folder" | "FolderOpen" | "ChevronRight" | "Library";

const glyph = <M>(h: HtmlBuilder<M>, name: Glyph, className = "") =>
	icon(h, name, lucide[name], { size: 15, class: `shrink-0 ${className}` });

export type TreeConfig<M> = Readonly<{
	nodes: ReadonlyArray<FolderNode>;
	/** Every note, for the "All notes" row. */
	allCount: number;
	/** None = "All notes". */
	selected: string | null;
	toggled: ReadonlyArray<string>;
	href: (folder: string | null) => string;
	onToggle: (path: string) => M;
	/** Present while a note is being dragged: rows accept the drop. */
	drop:
		| Readonly<{
				target: string | null;
				onEnter: (folder: string) => M;
				onLeave: (folder: string) => M;
				onDrop: (folder: string) => M;
		  }>
		| undefined;
}>;

const countBadge = <M>(h: HtmlBuilder<M>, n: number) =>
	h.span(
		[h.Class("ml-auto pl-2 text-xs tabular-nums text-base-content/50")],
		[String(n)],
	);

const row = <M>(
	h: HtmlBuilder<M>,
	config: TreeConfig<M>,
	key: string,
	folder: string | null,
	depth: number,
	label: string,
	count: number,
	toggle: Html,
	leading: Html,
): Html => {
	const current = config.selected === folder;
	const dropFolder = folder ?? "";
	const dropping = config.drop?.target === dropFolder;
	return h.li(
		[
			h.Key(key),
			h.DataAttribute("testid", "folder-row"),
			h.DataAttribute("folder", dropFolder),
			h.Class(
				`flex items-center gap-0.5 rounded-field ${dropping ? "bg-primary/15 outline-2 outline-dashed outline-primary" : config.drop ? "outline-1 outline-dashed outline-base-300" : ""}`,
			),
			h.Style({ paddingInlineStart: `${(depth - 1) * 0.875}rem` }),
			...(config.drop
				? [
						h.AllowDrop(),
						h.OnDragEnter(config.drop.onEnter(dropFolder)),
						h.OnDragLeave(config.drop.onLeave(dropFolder)),
						h.OnDrop(config.drop.onDrop(dropFolder)),
					]
				: []),
		],
		[
			toggle,
			h.a(
				[
					h.Href(config.href(folder)),
					h.Class(
						`flex min-w-0 flex-1 items-center gap-2 rounded-field px-2 py-1.5 text-sm transition-colors ${current ? "bg-base-200 font-semibold text-base-content" : "text-base-content/80 hover:bg-base-200/60"}`,
					),
					...(current ? [h.AriaCurrent("page")] : []),
					h.AriaLabel(
						i18next.t("knowledge.folders.folderLabel", { name: label, count }),
					),
				],
				[leading, h.span([h.Class("truncate")], [label]), countBadge(h, count)],
			),
		],
	);
};

export const folderTreeView = <M>(
	h: HtmlBuilder<M>,
	config: TreeConfig<M>,
): Html => {
	const spacer = h.span([h.Class("w-6 shrink-0")], []);
	return h.ul(
		[h.DataAttribute("testid", "folder-tree"), h.Class("space-y-0.5")],
		[
			row(
				h,
				config,
				"__all",
				null,
				1,
				i18next.t("knowledge.folders.all"),
				config.allCount,
				spacer,
				glyph(h, "Library", "text-base-content/60"),
			),
			...visibleNodes(config.nodes, config.toggled).map((node) => {
				const open = isExpanded(node.path, config.toggled);
				const toggle = node.hasChildren
					? h.button(
							[
								h.Type("button"),
								h.Class(
									"btn btn-ghost btn-xs btn-square h-6 min-h-6 w-6 text-base-content/60",
								),
								h.AriaExpanded(open),
								h.AriaLabel(
									i18next.t(
										open
											? "knowledge.folders.collapse"
											: "knowledge.folders.expand",
										{ name: node.name },
									),
								),
								h.OnClick(config.onToggle(node.path)),
							],
							[
								glyph(
									h,
									"ChevronRight",
									`transition-transform ${open ? "rotate-90" : ""}`,
								),
							],
						)
					: spacer;
				return row(
					h,
					config,
					node.path,
					node.path,
					node.depth + 1,
					node.name,
					node.total,
					toggle,
					glyph(
						h,
						open && node.hasChildren ? "FolderOpen" : "Folder",
						"text-primary/70",
					),
				);
			}),
		],
	);
};
